import React, { useState, useEffect } from "react";
import {
  Church,
  Users,
  Heart,
  Globe,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  User,
  MapPin,
  Clock,
  Calendar,
  ChevronDown,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import homePageImage from "../assets/home-page-image.png";
import churchAltarImage from "../assets/church-altar.jpg";
import aboutUsImage1 from "../assets/about-us-img1.jpg";
import aboutUsImage2 from "../assets/about-us-img2.jpg";
import aboutUsImage3 from "../assets/about-us-img3.jpg";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";
import Linkify from "linkify-react";

const About = () => {
  const { user } = useAuth();
  const [clergy, setClergy] = useState([]);
  const [isEditingClergy, setIsEditingClergy] = useState(false);
  const [editingClergy, setEditingClergy] = useState(null);
  const [showClergyModal, setShowClergyModal] = useState(false);
  const [editClergyForm, setEditClergyForm] = useState({
    name: "",
    title: "",
    description: "",
    image: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clergyLoading, setClergyLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("success");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  // Former Vicars state
  const [formerVicars, setFormerVicars] = useState([]);
  const [isEditingFormerVicars, setIsEditingFormerVicars] = useState(false);
  const [editingFormerVicar, setEditingFormerVicar] = useState(null);
  const [showFormerVicarModal, setShowFormerVicarModal] = useState(false);
  const [editFormerVicarForm, setEditFormerVicarForm] = useState({
    name: "",
    position: "",
    start_month: "",
    start_year: "",
    end_month: "",
    end_year: "",
  });
  const [formerVicarsLoading, setFormerVicarsLoading] = useState(true);

  // Managing Committee state
  const [managingCommittee, setManagingCommittee] = useState([]);
  const [managingCommitteeLoading, setManagingCommitteeLoading] =
    useState(true);
  const [expandedCommittee, setExpandedCommittee] = useState(null);

  // State for expandable rows in mobile view
  const [expandedFormerVicars, setExpandedFormerVicars] = useState(new Set());

  useEffect(() => {
    fetchClergy();
    fetchFormerVicars();
    fetchManagingCommittee();
  }, []);

  const months = [
    { value: "Jan", label: "January" },
    { value: "Feb", label: "February" },
    { value: "Mar", label: "March" },
    { value: "Apr", label: "April" },
    { value: "May", label: "May" },
    { value: "Jun", label: "June" },
    { value: "Jul", label: "July" },
    { value: "Aug", label: "August" },
    { value: "Sep", label: "September" },
    { value: "Oct", label: "October" },
    { value: "Nov", label: "November" },
    { value: "Dec", label: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  const fetchClergy = async () => {
    try {
      setClergyLoading(true);
      const response = await api.get("/api/clergy");
      if (response.data && Array.isArray(response.data)) {
        // Filter out null values and ensure all required fields exist
        const validClergy = response.data
          .filter(
            (member) => member && member.name && member.title && member.id
          )
          .map((member) => ({
            ...member,
            image: member.image || null,
            description: member.description || "",
          }));
        setClergy(validClergy);
      } else {
        setClergy([]);
      }
    } catch (error) {
      console.error("Error fetching clergy:", error);
      setClergy([]);
    } finally {
      setClergyLoading(false);
    }
  };

  const fetchFormerVicars = async () => {
    try {
      setFormerVicarsLoading(true);
      const response = await api.get("/api/former-vicars");
      if (response.data && Array.isArray(response.data)) {
        setFormerVicars(response.data);
      } else {
        setFormerVicars([]);
      }
    } catch (error) {
      console.error("Error fetching former vicars:", error);
      setFormerVicars([]);
    } finally {
      setFormerVicarsLoading(false);
    }
  };

  const fetchManagingCommittee = async () => {
    try {
      setManagingCommitteeLoading(true);
      const response = await api.get("/api/organizations");
      if (response.data && Array.isArray(response.data)) {
        // Filter for Managing Committee only
        const committeeData = response.data.filter(
          (org) => org.organization_type === "Managing Committee"
        );
        setManagingCommittee(committeeData);
      } else {
        setManagingCommittee([]);
      }
    } catch (error) {
      console.error("Error fetching managing committee:", error);
      setManagingCommittee([]);
    } finally {
      setManagingCommitteeLoading(false);
    }
  };

  const handleEditClergy = (clergyMember = null) => {
    setIsEditingClergy(true);
    setShowClergyModal(true);
    if (clergyMember) {
      setEditingClergy(clergyMember);
      setEditClergyForm({
        name: clergyMember.name,
        title: clergyMember.title,
        description: clergyMember.description,
        image: clergyMember.image,
      });
    } else {
      setEditingClergy(null);
      setEditClergyForm({
        name: "",
        title: "",
        description: "",
        image: "",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingClergy(false);
    setEditingClergy(null);
    setShowClergyModal(false);
    setEditClergyForm({
      name: "",
      title: "",
      description: "",
      image: "",
    });
    setImageFile(null);
  };

  const showCustomDialog = (message, type = "success") => {
    setDialogMessage(message);
    setDialogType(type);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setDialogMessage("");
  };

  const showConfirmationDialog = (message, action) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => action);
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmDialogMessage("");
    setConfirmDialogAction(null);
  };

  const handleConfirmAction = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    closeConfirmDialog();
  };

  // Helper function for expandable rows
  const toggleExpandedFormerVicar = (id) => {
    const newSet = new Set(expandedFormerVicars);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedFormerVicars(newSet);
  };

  // Helper function for expandable committee
  const toggleExpandedCommittee = (orgId) => {
    setExpandedCommittee(expandedCommittee === orgId ? null : orgId);
  };

  // Linkify options for managing committee
  const linkifyOptions = {
    attributes: {
      class: "text-red-600 hover:text-red-800 underline break-all",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditClergyForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleSaveClergy = async () => {
    if (!editClergyForm.name || !editClergyForm.title) {
      showCustomDialog(
        "Please fill in all required fields (Name and Title)",
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      let updatedData = { ...editClergyForm };

      // Handle image upload if new image is selected
      if (imageFile) {
        try {
          // Step 1: Get upload signature
          const signatureResponse = await api.post(
            "/api/clergy/upload-signature"
          );
          const { signature, timestamp, cloudName, apiKey, uploadParams } =
            signatureResponse.data;

          // Step 2: Upload directly to Cloudinary
          const formData = new FormData();
          formData.append("file", imageFile);
          formData.append("signature", signature);
          formData.append("timestamp", timestamp);
          formData.append("api_key", apiKey);

          // Add upload parameters
          Object.keys(uploadParams).forEach((key) => {
            if (key !== "timestamp") {
              formData.append(key, uploadParams[key]);
            }
          });

          const cloudinaryResponse = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
            {
              method: "POST",
              body: formData,
            }
          );

          if (!cloudinaryResponse.ok) {
            throw new Error("Failed to upload image to Cloudinary");
          }

          const cloudinaryResult = await cloudinaryResponse.json();
          updatedData.image = cloudinaryResult.secure_url;
        } catch (error) {
          console.error("Clergy image upload error:", error);
          throw new Error("Failed to upload clergy image. Please try again.");
        }
      }

      let response;
      if (editingClergy) {
        // Update existing clergy
        response = await api.put(
          `/api/clergy/${editingClergy.id}`,
          updatedData
        );
        setClergy((prev) =>
          prev.map((c) =>
            c.id === editingClergy.id
              ? {
                  ...response.data,
                  image: response.data.image || "",
                  description: response.data.description || "",
                }
              : c
          )
        );
      } else {
        // Add new clergy
        response = await api.post("/api/clergy", updatedData);
        setClergy((prev) => [
          ...prev,
          {
            ...response.data,
            image: response.data.image || "",
            description: response.data.description || "",
          },
        ]);
      }

      handleCancelEdit();
      showCustomDialog("Clergy information saved successfully!");
    } catch (error) {
      console.error("Error saving clergy:", error);
      showCustomDialog(
        "Error saving clergy information. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClergy = (clergyId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/clergy/${clergyId}`);
        setClergy((prev) => prev.filter((c) => c.id !== clergyId));
        showCustomDialog("Clergy member deleted successfully!");
      } catch (error) {
        console.error("Error deleting clergy:", error);
        showCustomDialog(
          "Error deleting clergy member. Please try again.",
          "error"
        );
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this clergy member?",
      deleteAction
    );
  };

  // Former Vicars handlers
  const handleEditFormerVicar = (formerVicar = null) => {
    setIsEditingFormerVicars(true);
    setShowFormerVicarModal(true);
    if (formerVicar) {
      setEditingFormerVicar(formerVicar);
      const [startMonth, startYear] = formerVicar.start_period.split("-");
      const [endMonth, endYear] = formerVicar.end_period.split("-");
      setEditFormerVicarForm({
        name: formerVicar.name,
        position: formerVicar.position,
        start_month: startMonth,
        start_year: startYear,
        end_month: endMonth,
        end_year: endYear,
      });
    } else {
      setEditingFormerVicar(null);
      setEditFormerVicarForm({
        name: "",
        position: "",
        start_month: "",
        start_year: "",
        end_month: "",
        end_year: "",
      });
    }
  };

  const handleCancelFormerVicarEdit = () => {
    setIsEditingFormerVicars(false);
    setEditingFormerVicar(null);
    setShowFormerVicarModal(false);
    setEditFormerVicarForm({
      name: "",
      position: "",
      start_month: "",
      start_year: "",
      end_month: "",
      end_year: "",
    });
  };

  const handleFormerVicarInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormerVicarForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveFormerVicar = async () => {
    if (
      !editFormerVicarForm.name ||
      !editFormerVicarForm.position ||
      !editFormerVicarForm.start_month ||
      !editFormerVicarForm.start_year ||
      !editFormerVicarForm.end_month ||
      !editFormerVicarForm.end_year
    ) {
      showCustomDialog("Please fill in all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      const formerVicarData = {
        name: editFormerVicarForm.name,
        position: editFormerVicarForm.position,
        start_period: `${editFormerVicarForm.start_month}-${editFormerVicarForm.start_year}`,
        end_period: `${editFormerVicarForm.end_month}-${editFormerVicarForm.end_year}`,
      };

      let response;
      if (editingFormerVicar) {
        // Update existing former vicar
        response = await api.put(
          `/api/former-vicars/${editingFormerVicar.id}`,
          formerVicarData
        );
        setFormerVicars((prev) =>
          prev.map((fv) =>
            fv.id === editingFormerVicar.id ? response.data : fv
          )
        );
      } else {
        // Add new former vicar
        response = await api.post("/api/former-vicars", formerVicarData);
        setFormerVicars((prev) => [...prev, response.data]);
      }

      handleCancelFormerVicarEdit();
      showCustomDialog("Former vicar information saved successfully!");
    } catch (error) {
      console.error("Error saving former vicar:", error);
      showCustomDialog(
        "Error saving former vicar information. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFormerVicar = (formerVicarId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/former-vicars/${formerVicarId}`);
        setFormerVicars((prev) => prev.filter((fv) => fv.id !== formerVicarId));
        showCustomDialog("Former vicar deleted successfully!");
      } catch (error) {
        console.error("Error deleting former vicar:", error);
        showCustomDialog(
          "Error deleting former vicar. Please try again.",
          "error"
        );
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this former vicar?",
      deleteAction
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">About Our Church</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            San Antonio St. Mary’s Malankara (Indian) Orthodox Church is rooted
            in ancient Christian traditions dating back to the Apostle St.
            Thomas who brought Christianity to India in 52 AD.
          </p>
        </div>
      </section>

      <section id="our-history" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our History
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our parish belongs to DSWA/Diocese of South-West America of
                Indian Orthodox Syrian Church. This Parish was established in
                2010 as a small chapel - with a humble begriming. Ever since the
                inception, we’re truly blessed and showered with abundant Grace
                of Lord Almighty and the protection of His invisible "hands"
                throughout our journey thus far.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                June 28, 2010: First Holy Qurbana was celebrated by Rev. Fr. M.M
                Zachariah at the St. Mary’s Orthodox Christian Chapel near
                Medical Center in San Antonio
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                December 9, 2010: Our small congregation was promised to be
                recongnied as a Chapel, at the meeting held at the aramana with
                HG Alexios Mar Eusebius Metropolitan.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                December 26, 2010: Rev Fr. Mathukutty Varghese conducted Holy
                Qurbana and read approval Kalpana (pastoral letter) from the
                Diocesan bishop recognizing our congregations as St. Mary's
                Indian Orthodox Syrian Chapel.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                January 22, 2011: Diocesan Bishop, HG Alexios Mar Eusebios made
                the maiden visit to our Chapel on “St. Mary’s feast for the
                Seeds” and celebrated Holy Qurbana.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                November 19, 2011: Through prayers and guidance of the Holy
                Spirit, H. G. Alexios Mar Eusebius, then the Diocesan Bishop
                declared & elevated Chapel as St. Mary's Malankara Orthodox
                Syrian Church of San Antonio (Order/Kalpana#: DSWA/CH/58/2011) -
                which is a full-fledged parish under SW Diocese of America. And,
                appointed Rev. Fr. Dr. V C Varghese as the new vicar to the
                parish.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                December 28, 2012: St. Mary's Church San Antonio purchased a
                newly renovated building at 4990 View Drive in San Antonio.
                Diocesan Bishop HG Alexios Mar Eusebios himself signed the
                "Article of Deed" at the closing on behalf of Malankara
                Metropolitan as per the Church constitution (perhaps this is the
                first time ever any DSWA Church’s Deed was signed by the bishop
                himself).
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                August 15, 2015: Diocesan Bishop HG Alexios Mar Eusebius
                Metropolitan, on the Feast of Assumption of Theotokos-2015, laid
                the foundation stone for the new church building.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                May 14, 2019: Church secured a neighboring property for the
                additional 40+ car parking, and the phase 1 fellowship hall
                construction began. We entered the last phase of getting the
                plans approval by the City.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Sept 8th 2019: Foundation was poured for our dream church (in
                phase 1)
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Dec 29, 30 2023: Church Consecration by Holy Muron (Holy oil)
              </p>
            </div>
            <div>
              <img
                src={homePageImage}
                alt="Ancient Church"
                className="w-full rounded-lg shadow-lg mb-4"
              />
              <img
                src={churchAltarImage}
                alt="Ancient Church"
                className="w-full rounded-lg shadow-lg mb-4"
              />
              <img
                src={aboutUsImage3}
                alt="Ancient Church"
                className="w-full rounded-lg shadow-lg mb-4"
              />
              <img
                src={aboutUsImage2}
                alt="Ancient Church"
                className="w-full rounded-lg shadow-lg mb-4"
              />
              <img
                src={aboutUsImage1}
                alt="Ancient Church"
                className="w-full rounded-lg shadow-lg mb-4"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Church className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Tradition
              </h3>
              <p className="text-gray-600">
                Preserving the ancient traditions and liturgy passed down
                through generations.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Community
              </h3>
              <p className="text-gray-600">
                Building a strong, supportive community centered on faith and
                fellowship.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Service
              </h3>
              <p className="text-gray-600">
                Serving our community and those in need with love and
                compassion.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Global Unity
              </h3>
              <p className="text-gray-600">
                Connected to Orthodox churches worldwide while maintaining our
                unique heritage.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="our-clergy" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Clergy
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-4"></div>
            {user && clergy.length > 0 && (
              <button
                onClick={() => setIsEditingClergy(!isEditingClergy)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 mx-auto"
              >
                {isEditingClergy ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
                {isEditingClergy ? "Cancel" : "Edit Clergy"}
              </button>
            )}
          </div>

          {clergyLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">
                Loading clergy information...
              </p>
            </div>
          ) : (
            <>
              {clergy.length === 0 ? (
                <div className="text-center py-12">
                  <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Clergy Information Available
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Information about our clergy will appear here once added.
                  </p>
                  {user && (
                    <button
                      onClick={() => handleEditClergy()}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Plus className="w-5 h-5" />
                      Add First Clergy Member
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {isEditingClergy && (
                    <div className="mb-8 text-center">
                      <button
                        onClick={() => handleEditClergy()}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        <Plus className="w-4 h-4" />
                        Add Clergy
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {clergy
                      .filter((member) => member && member.id)
                      .map((clergyMember) => (
                        <div
                          key={clergyMember.id}
                          className="text-center relative"
                        >
                          {isEditingClergy && (
                            <div className="absolute top-0 right-0 flex gap-2 z-10">
                              <button
                                onClick={() => handleEditClergy(clergyMember)}
                                className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteClergy(clergyMember.id)
                                }
                                className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          {clergyMember.image ? (
                            <img
                              src={clergyMember.image}
                              alt={clergyMember.name || "Clergy Member"}
                              className="w-48 h-48 rounded-full mx-auto mb-4 object-cover"
                            />
                          ) : (
                            <div className="w-48 h-48 rounded-full mx-auto mb-4 bg-gray-200 flex items-center justify-center">
                              <User className="w-24 h-24 text-gray-400" />
                            </div>
                          )}
                          <h3 className="text-xl font-semibold text-gray-900 mb-2 min-h-[30px]">
                            {clergyMember.name || "Unknown"}
                          </h3>
                          <p className="text-lg text-red-600 mb-4 min-h-[60px]">
                            {clergyMember.title || "Title"}
                          </p>
                          <p className="text-gray-600 max-w-md mx-auto">
                            {clergyMember.description ||
                              "No description available."}
                          </p>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Edit/Add Clergy Modal */}
          {showClergyModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[97%] overflow-y-auto mx-4">
                <h3 className="text-2xl font-bold mb-4">
                  {editingClergy ? "Edit Clergy" : "Add Clergy"}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editClergyForm.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editClergyForm.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={editClergyForm.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image URL or Upload
                    </label>
                    <input
                      type="text"
                      name="image"
                      value={editClergyForm.image}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent mb-2"
                      placeholder="Enter image URL or upload below"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Supported formats: JPEG, PNG, GIF, WebP (max 20MB)
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveClergy}
                    disabled={loading}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Dialog Modal */}
          {showDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
                <div className="flex items-center mb-4">
                  {dialogType === "success" ? (
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {dialogType === "success" ? "Success" : "Error"}
                  </h3>
                </div>
                <p className="text-gray-700 mb-6">{dialogMessage}</p>
                <div className="flex justify-end">
                  <button
                    onClick={closeDialog}
                    className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Confirmation Dialog */}
          {showConfirmDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Delete
                  </h3>
                </div>
                <p className="text-gray-700 mb-6">{confirmDialogMessage}</p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeConfirmDialog}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAction}
                    className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Former Vicars Section */}
      <section id="former-vicars" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Former Vicars
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-4"></div>
            {user && formerVicars.length > 0 && (
              <button
                onClick={() => setIsEditingFormerVicars(!isEditingFormerVicars)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 mx-auto"
              >
                {isEditingFormerVicars ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
                {isEditingFormerVicars ? "Cancel" : "Edit Former Vicars"}
              </button>
            )}
          </div>

          {formerVicarsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading former vicars...</p>
            </div>
          ) : (
            <>
              {formerVicars.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Former Vicars Listed
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Information about former vicars will appear here once added.
                  </p>
                  {user && (
                    <button
                      onClick={() => handleEditFormerVicar()}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Plus className="w-5 h-5" />
                      Add First Former Vicar
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {isEditingFormerVicars && (
                    <div className="mb-8 text-center">
                      <button
                        onClick={() => handleEditFormerVicar()}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        <Plus className="w-4 h-4" />
                        Add Former Vicar
                      </button>
                    </div>
                  )}

                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full bg-white rounded-lg shadow-lg">
                      <thead className="bg-red-600 text-white">
                        <tr>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-red-600">
                            Name
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-red-600">
                            Position
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-red-600">
                            Start Period
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-red-600">
                            End Period
                          </th>
                          {isEditingFormerVicars && (
                            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-red-600">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formerVicars.map((formerVicar) => (
                          <tr key={formerVicar.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium text-gray-900 border border-t-0 border-red-600">
                              {formerVicar.name}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600 border border-t-0 border-red-600">
                              {formerVicar.position}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600 border border-t-0 border-red-600">
                              {formerVicar.start_period}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600 border border-t-0 border-red-600">
                              {formerVicar.end_period}
                            </td>
                            {isEditingFormerVicars && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 border border-t-0 border-red-600">
                                <div className="flex gap-2 text-center">
                                  <button
                                    onClick={() =>
                                      handleEditFormerVicar(formerVicar)
                                    }
                                    className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition-colors"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteFormerVicar(formerVicar.id)
                                    }
                                    className="bg-red-600 text-white p-2 rounded hover:bg-red-700 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Expandable List View */}
                  <div className="md:hidden bg-white rounded-lg shadow-lg">
                    {formerVicars.map((formerVicar) => {
                      const isExpanded = expandedFormerVicars.has(
                        formerVicar.id
                      );
                      return (
                        <div
                          key={formerVicar.id}
                          className="border-b border-gray-200 last:border-b-0"
                        >
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                            onClick={() =>
                              toggleExpandedFormerVicar(formerVicar.id)
                            }
                          >
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {formerVicar.name}
                              </h3>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isEditingFormerVicars && (
                                <div className="flex space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditFormerVicar(formerVicar);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 p-1"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFormerVicar(formerVicar.id);
                                    }}
                                    className="text-red-600 hover:text-red-900 p-1"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-4 pb-4 space-y-2">
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Position:
                                </span>
                                <p className="text-sm text-gray-900 mt-1">
                                  {formerVicar.position}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Start Period:
                                </span>
                                <p className="text-sm text-gray-900 mt-1">
                                  {formerVicar.start_period}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  End Period:
                                </span>
                                <p className="text-sm text-gray-900 mt-1">
                                  {formerVicar.end_period}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {/* Former Vicar Modal */}
          {showFormerVicarModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[97%] overflow-y-auto mx-4">
                <h3 className="text-2xl font-bold mb-4">
                  {editingFormerVicar
                    ? "Edit Former Vicar"
                    : "Add Former Vicar"}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editFormerVicarForm.name}
                      onChange={handleFormerVicarInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position *
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={editFormerVicarForm.position}
                      onChange={handleFormerVicarInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Period *
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="start_month"
                        value={editFormerVicarForm.start_month}
                        onChange={handleFormerVicarInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        required
                      >
                        <option value="">Month</option>
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.value}
                          </option>
                        ))}
                      </select>
                      <select
                        name="start_year"
                        value={editFormerVicarForm.start_year}
                        onChange={handleFormerVicarInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        required
                      >
                        <option value="">Year</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Period *
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="end_month"
                        value={editFormerVicarForm.end_month}
                        onChange={handleFormerVicarInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        required
                      >
                        <option value="">Month</option>
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.value}
                          </option>
                        ))}
                      </select>
                      <select
                        name="end_year"
                        value={editFormerVicarForm.end_year}
                        onChange={handleFormerVicarInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        required
                      >
                        <option value="">Year</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={handleCancelFormerVicarEdit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveFormerVicar}
                    disabled={loading}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Managing Committee Section */}
      <section id="managing-committee" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Managing Committee
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {managingCommitteeLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">
                Loading managing committee...
              </p>
            </div>
          ) : managingCommittee.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Managing Committee Information Available
              </h3>
              <p className="text-gray-500">
                Information about the managing committee will appear here once
                added.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {managingCommittee.map((org) => (
                <div
                  key={org._id}
                  className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                >
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpandedCommittee(org._id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                          <Users className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-xl font-semibold text-gray-900">
                            {org.title}
                          </h4>
                          <p className="text-gray-600 text-sm">
                            Click to view details
                          </p>
                        </div>
                      </div>
                      <ChevronRight
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                          expandedCommittee === org._id ? "rotate-90" : ""
                        }`}
                      />
                    </div>
                  </div>

                  {expandedCommittee === org._id && (
                    <div className="px-6 pb-6 border-t border-gray-200">
                      <div className="pt-6 space-y-4">
                        {org.description && (
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2">
                              Description
                            </h5>
                            <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                              <Linkify options={linkifyOptions}>
                                {org.description}
                              </Linkify>
                            </p>
                          </div>
                        )}

                        {org.members && (
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                              <User className="w-4 h-4 mr-1" />
                              Members
                            </h5>
                            <div className="text-gray-600 whitespace-pre-line">
                              {org.members}
                            </div>
                          </div>
                        )}

                        {org.announcements && (
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Announcements
                            </h5>
                            <p className="text-gray-600 whitespace-pre-line">
                              <Linkify options={linkifyOptions}>
                                {org.announcements}
                              </Linkify>
                            </p>
                          </div>
                        )}

                        {org.createdAt && (
                          <div className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                            Created:{" "}
                            {new Date(org.createdAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Visit us section */}
      <section id="visit-us" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Visit Us</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                St. Mary’s Indian Orthodox Church
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-red-600 mr-3 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Address:</p>
                    <p className="text-gray-600">4990 View Dr, San Antonio,</p>
                    <p className="text-gray-600">Texas 78228, United States</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="w-6 h-6 text-red-600 mr-3 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Service Timing:
                    </p>
                    <p className="text-gray-600">Sunday: 8:30 AM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg h-96 overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3468.8844776344424!2d-98.6159842846872!3d29.440572782069245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865c5dc526826103%3A0x6e0e4f670000f3fa!2s4990%20View%20Dr%2C%20San%20Antonio%2C%20TX%2078228%2C%20USA!5e0!3m2!1sen!2sus!4v1690000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="St. Mary’s Malankara (Indian) Orthodox Church Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
